import React from 'react';
import { Link } from 'react-router-dom';

import MapImage from '../images/NEMAP.png';
import AnkitPhoto from '../images/Ankit.png';
import CostelloPhoto from '../images/Costello.png';

const MAP_PDF_URL =
  'https://thespur.org/static/media/2026NEMAP.52c87a8a2dddfbdb328a.pdf';

const NeAgTechMap = () => {
  return (
    <div className="pub-page">
      {/* Breadcrumb */}
      <div className="pub-breadcrumb">
        <div className="container">
          <Link
            to="/resources"
            className="pub-breadcrumb-link"
          >
            ← Back to Resources
          </Link>
        </div>
      </div>

      {/* Page header */}
      <div className="pub-header">
        <div className="container">
          <h1 className="pub-title">
            Nebraska AgTech Innovation Ecosystem Map 2026
          </h1>

          <p className="pub-date">
            April 2026
          </p>
        </div>
      </div>

      <div className="pub-body">
        {/* Full-width map image */}
        <div className="pub-map-full-section">
          <div className="container">
            <div className="pub-map-wrapper">
              <img
                src={MapImage}
                alt="Nebraska AgTech Innovation Ecosystem Map 2026"
                className="pub-map-img"
              />
            </div>

            {/* Download options */}
            <div className="pub-download-row">
              <a
                href={MapImage}
                target="_blank"
                rel="noopener noreferrer"
                className="pub-download-btn"
              >
                <i
                  className="fas fa-image"
                  aria-hidden="true"
                />

                <span>
                  Download high-resolution image
                </span>
              </a>

              <span
                className="pub-download-divider"
                aria-hidden="true"
              />

              <a
                href={MAP_PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="pub-download-btn"
              >
                <i
                  className="fas fa-file-pdf"
                  aria-hidden="true"
                />

                <span>
                  Download article PDF
                </span>
              </a>
            </div>
          </div>
        </div>

        {/* Two-column layout: About + Sidebar */}
        <div className="container pub-layout">
          {/* Main content */}
          <main className="pub-main">
            <section className="pub-section">
              <h2 className="pub-section-title">
                About the Map
              </h2>

              <p className="pub-paragraph">
                The 2026 Nebraska AgTech Innovation Ecosystem Map,
                developed by Spur Ventures and The Combine, builds on
                prior efforts and highlights how innovation is driving
                change in Nebraska&apos;s agricultural sector.
              </p>

              <p className="pub-paragraph">
                Nebraska&apos;s AgTech ecosystem has quietly become one
                of the most active in the country, adding more than a
                dozen new AgTech startups since 2024. This growth
                reflects a rise in new startup formation. It also
                signals increasing technological maturity and stronger
                ecosystem support for agricultural innovation.
              </p>

              <p className="pub-paragraph">
                We categorize startups based on the nature of their
                technology, innovation approach, and primary value
                proposition across five key segments:
              </p>

              <ul className="pub-list">
                <li>
                  <strong>
                    Robotics and Automation
                  </strong>{' '}
                  startups offer solutions intended to reduce manual
                  labor, increase operational efficiency, and perform
                  repetitive, hazardous, or precision-dependent tasks.
                  These companies rely heavily on in situ hardware,
                  embedded systems, AI/ML, sensors, telemetry, and
                  autonomous equipment operating directly on farms or
                  in processing labs.
                </li>

                <li>
                  <strong>
                    Precision Ag and Decision Support
                  </strong>{' '}
                  startups use data-driven tools to improve farm-level
                  decision-making for crops and livestock. Through
                  integrated software platforms, sensing technologies,
                  and analytics, these companies help producers monitor
                  field conditions, detect risks, and optimize the
                  application of inputs such as water, fertilizer, feed,
                  and chemicals.
                </li>

                <li>
                  <strong>
                    Animal Health Monitoring
                  </strong>{' '}
                  startups focus on tracking livestock health,
                  productivity, and behavior using wearables,
                  diagnostics, and real-time data systems. Their
                  technologies aim to improve disease detection,
                  reproductive performance, feed efficiency, and
                  overall herd-management outcomes.
                </li>

                <li>
                  <strong>
                    Ag Marketplace and Finance
                  </strong>{' '}
                  startups use digital and non-digital platforms to
                  facilitate transactions and capital flows across the
                  agricultural value chain. This category includes
                  companies enabling the buying and selling of
                  agricultural products, land, and services, as well as
                  those providing financial tools for credit access,
                  risk management, and alternative financing.
                </li>

                <li>
                  <strong>
                    Ag Biotech
                  </strong>{' '}
                  startups leverage advances in genetics,
                  bioengineering, biological inputs, and microbial
                  science to enhance crop and animal performance. Their
                  innovations include improved breeding technologies,
                  beneficial microbes, disease-prevention tools, and
                  sustainable food and product development.
                </li>
              </ul>

              <p className="pub-paragraph pub-paragraph-spaced">
                Beyond startups, Nebraska&apos;s broader AgTech
                ecosystem continues to mature. A growing network of
                university programs and initiatives, incubators,
                investors, corporations, and nonprofit organizations
                plays a critical role in enabling innovation and
                commercialization.
              </p>

              <p className="pub-paragraph">
                This map is specifically focused on actors that
                actively participate in innovation or entrepreneurial
                ecosystems. These actors provide startups with access
                to research infrastructure, field validation,
                mentorship, early-stage capital, and pathways to
                market, strengthening Nebraska&apos;s position as a
                leading hub for agricultural innovation.
              </p>

              <p className="pub-author-note">
                The 2026 Nebraska AgTech Innovation Ecosystem Map was
                developed by Ankit Chandra, Director of Spur Ventures,
                and Brennan Costello, Director of The Combine, with
                support from the National Science Foundation&apos;s
                AgTech Engine in North Dakota.
              </p>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="pub-sidebar">
            <h3 className="pub-sidebar-title">
              Authors
            </h3>

            {/* Ankit Chandra */}
            <div className="pub-author-card">
              <div className="pub-author-photo-wrap">
                <img
                  src={AnkitPhoto}
                  alt="Ankit Chandra"
                  className="pub-author-photo"
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';

                    const fallback =
                      event.currentTarget.nextElementSibling;

                    if (fallback) {
                      fallback.style.display = 'flex';
                    }
                  }}
                />

                <div
                  className="pub-author-photo-fallback"
                  style={{ display: 'none' }}
                  aria-hidden="true"
                >
                  AC
                </div>
              </div>

              <div className="pub-author-info">
                <p className="pub-author-name">
                  Ankit Chandra
                </p>

                <p className="pub-author-role">
                  Director, Spur Ventures
                </p>

                <p className="pub-author-org">
                  University of Nebraska–Lincoln
                </p>
              </div>
            </div>

            {/* Brennan Costello */}
            <div className="pub-author-card">
              <div className="pub-author-photo-wrap">
                <img
                  src={CostelloPhoto}
                  alt="Brennan Costello"
                  className="pub-author-photo"
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';

                    const fallback =
                      event.currentTarget.nextElementSibling;

                    if (fallback) {
                      fallback.style.display = 'flex';
                    }
                  }}
                />

                <div
                  className="pub-author-photo-fallback"
                  style={{ display: 'none' }}
                  aria-hidden="true"
                >
                  BC
                </div>
              </div>

              <div className="pub-author-info">
                <p className="pub-author-name">
                  Brennan Costello
                </p>

                <p className="pub-author-role">
                  Director, The Combine
                </p>

                <p className="pub-author-org">
                  Nebraska
                </p>
              </div>
            </div>

            {/* Share */}
            <div className="pub-share-section">
              <h3 className="pub-sidebar-title">
                Share
              </h3>

              <div className="pub-share-links">
                <a
                  href="https://www.linkedin.com/company/the-spur-ventures"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pub-share-btn"
                >
                  <i
                    className="fab fa-linkedin-in"
                    aria-hidden="true"
                  />

                  LinkedIn
                </a>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default NeAgTechMap;